import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveImg, formatPrice, formatMileage, COUNTRY_FLAGS, BODY_LABELS, FUEL_LABELS, TRANSMISSION_LABELS } from "@/lib/api";
import { useApp, t } from "@/lib/AppContext";
import FeedbackDialog from "@/components/FeedbackDialog";
import { ChevronLeft, ChevronRight, Calendar, Gauge, Fuel, Cog, MapPin, Truck, Award, FileText, Users, Send, MessageCircle, Phone } from "lucide-react";

export default function CarDetail() {
  const { slug } = useParams();
  const { content, settings } = useApp();
  const [car, setCar] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/cars/${slug}`)
      .then(r => setCar(r.data))
      .catch(() => setCar(null))
      .finally(() => setLoading(false));
    api.get(`/cars/${slug}/similar`).then(r => setSimilar(r.data || [])).catch(() => setSimilar([]));
  }, [slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-16"><div className="skeleton h-96" /></div>;
  if (!car) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="car-not-found">
      <h1 className="section-heading mb-4">Авто не найдено</h1>
      <Link to="/catalog" className="btn-primary-cta">К каталогу</Link>
    </div>
  );

  const photos = car.photos?.length ? car.photos : [];
  const carName = `${car.brand} ${car.model} ${car.year}`;
  const flag = COUNTRY_FLAGS[car.country_origin] || "";

  const specs = [
    { Icon: Calendar, label: "Год", value: car.year },
    { Icon: Gauge, label: "Пробег", value: formatMileage(car.mileage) },
    { Icon: Fuel, label: "Двигатель", value: FUEL_LABELS[car.fuel_type] || car.fuel_type },
    { Icon: Cog, label: "Коробка", value: TRANSMISSION_LABELS[car.transmission] || car.transmission },
    { Icon: Award, label: "Кузов", value: BODY_LABELS[car.body_type] || car.body_type },
    { Icon: MapPin, label: "Страна", value: `${flag} ${car.country_origin || "—"}` },
    { Icon: FileText, label: "ПТС", value: car.pts_type || "—" },
    { Icon: Users, label: "Владельцев", value: car.owners_count || 1 },
    { Icon: Award, label: "Эко-класс", value: car.euro_standard || "—" },
    { Icon: Truck, label: "Доставка", value: car.customs_cleared ? "Растаможен" : `~${car.delivery_days} дн.` },
  ];

  return (
    <div className="bg-[var(--color-bg-primary)]" data-testid="car-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Breadcrumbs */}
        <nav className="text-sm font-accent uppercase tracking-wider text-[var(--color-text-secondary)] mb-6">
          <Link to="/" className="hover:text-[var(--color-primary)]">Главная</Link>
          <span className="mx-2">/</span>
          <Link to="/catalog" className="hover:text-[var(--color-primary)]">Каталог</Link>
          <span className="mx-2">/</span>
          <span className="text-[var(--color-text-primary)]">{carName}</span>
        </nav>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
          {/* Gallery */}
          <div>
            <div className="relative aspect-[16/10] bg-white rounded-[var(--radius-lg)] overflow-hidden border-t-[3px]" style={{ borderColor: "var(--color-primary)" }}>
              {photos[photoIdx] ? (
                <img src={resolveImg(photos[photoIdx])} alt={carName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300 font-headings text-3xl">{car.brand}</div>
              )}
              {photos.length > 1 && (
                <>
                  <button onClick={() => setPhotoIdx(i => (i - 1 + photos.length) % photos.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center" aria-label="Предыдущее">
                    <ChevronLeft />
                  </button>
                  <button onClick={() => setPhotoIdx(i => (i + 1) % photos.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center" aria-label="Следующее">
                    <ChevronRight />
                  </button>
                </>
              )}
              <div className="absolute top-4 left-4 flex gap-2">
                {car.customs_cleared && <span className="status-badge" style={{ background: "var(--color-success)", color: "#FFFFFF" }}>Растаможен</span>}
                {car.euro_standard && <span className="status-badge" style={{ background: "rgba(28,58,107,0.9)", color: "#FFFFFF" }}>{car.euro_standard}</span>}
              </div>
            </div>
            {photos.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {photos.map((p, i) => (
                  <button key={i} onClick={() => setPhotoIdx(i)} className={`aspect-[4/3] rounded-md overflow-hidden border-2 transition-all`} style={{ borderColor: i === photoIdx ? "var(--color-primary)" : "transparent" }}>
                    <img src={resolveImg(p)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="bg-white rounded-[var(--radius-lg)] p-6 border-t-[3px]" style={{ borderColor: "var(--color-primary)", boxShadow: "var(--shadow-card)" }}>
              <h1 className="font-headings font-extrabold text-2xl md:text-3xl text-[var(--color-text-primary)]" data-testid="car-title">{carName}</h1>
              <div className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)] mt-1">
                {formatMileage(car.mileage)} · {BODY_LABELS[car.body_type] || ""} · {flag} {car.country_origin}
              </div>
              <div className="mt-5 mb-5 pb-5 border-b" style={{ borderColor: "var(--color-border)" }}>
                <div className="price-tag" style={{ fontSize: 36 }} data-testid="car-detail-price">{formatPrice(car.price)}</div>
                {!car.customs_cleared && car.delivery_days > 0 && (
                  <div className="text-xs text-[var(--color-text-secondary)] mt-1 flex items-center gap-1"><Truck size={12} /> Срок доставки ~{car.delivery_days} дней</div>
                )}
              </div>
              <button onClick={() => setFeedbackOpen(true)} className="btn-primary-cta w-full mb-2" data-testid="car-detail-cta">
                Оставить заявку
              </button>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {settings?.telegram_url && (
                  <a href={settings.telegram_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 py-2.5 rounded-[var(--radius-md)] text-white font-accent text-xs font-semibold uppercase tracking-wider" style={{ background: "#229ED9" }}>
                    <Send size={14} /> Telegram
                  </a>
                )}
                {settings?.whatsapp_url && (
                  <a href={settings.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 py-2.5 rounded-[var(--radius-md)] text-white font-accent text-xs font-semibold uppercase tracking-wider" style={{ background: "#25D366" }}>
                    <MessageCircle size={14} /> WhatsApp
                  </a>
                )}
              </div>
            </div>
          </aside>
        </div>

        {/* Specs */}
        <div className="grid md:grid-cols-2 gap-8 mt-12">
          <div>
            <h2 className="font-headings font-bold text-xl mb-5">Характеристики</h2>
            <div className="grid grid-cols-2 gap-3 bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
              {specs.map((s, i) => (
                <div key={i} className="flex items-start gap-2.5 py-2">
                  <s.Icon size={16} className="mt-0.5" style={{ color: "var(--color-secondary)" }} />
                  <div>
                    <div className="text-[10px] font-accent uppercase tracking-widest text-[var(--color-text-secondary)]">{s.label}</div>
                    <div className="font-headings font-semibold text-sm text-[var(--color-text-primary)]">{s.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-headings font-bold text-xl mb-5">Описание</h2>
            <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
              <p className="text-[var(--color-text-primary)] leading-relaxed">{car.description}</p>
            </div>
          </div>
        </div>
      </div>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} carName={carName} defaultMessage={`Заинтересовал автомобиль ${carName}`} />

      {similar.length > 0 && (
        <section className="py-12 md:py-16 bg-white border-t" style={{ borderColor: "var(--color-border)" }} data-testid="similar-cars">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-headings font-bold text-2xl md:text-3xl mb-8">Похожие автомобили</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {similar.map(sc => <SimilarCarCard key={sc.id} car={sc} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function SimilarCarCard({ car }) {
  const photo = car.photos?.[0] ? resolveImg(car.photos[0]) : null;
  return (
    <Link to={`/cars/${car.slug}`} className="car-card group" data-testid={`similar-${car.slug}`}>
      <div className="aspect-[16/10] bg-gray-100 overflow-hidden">
        {photo ? <img src={photo} alt={`${car.brand} ${car.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <div className="w-full h-full flex items-center justify-center text-gray-300">{car.brand}</div>}
      </div>
      <div className="p-4">
        <div className="font-headings font-bold text-sm line-clamp-1">{car.brand} {car.model}</div>
        <div className="text-xs text-[var(--color-text-secondary)] mb-2">{car.year} · {formatMileage(car.mileage)}</div>
        <div className="price-tag" style={{ fontSize: 18 }}>{formatPrice(car.price)}</div>
      </div>
    </Link>
  );
}
